import {
  UNHEDGED_COLOR_HEX,
  VENUE_WARN_COLOR_HEX
} from '../descalce/descalceColors';

/**
 * The per-row slice of the triage index a mark renders from (spec §6.6):
 * `counter` comes from `byRuleId` (a stuck counter belongs to a RULE),
 * `orphan` from `byPairId` (an orphaned automation fill belongs, at best, to a
 * PAIR).
 *
 * `undefined` is NOT "zero": it means the index could not be read, and the
 * surfaces render nothing while their header says so.
 */
export interface TriageRowCounts {
  counter: number;
  orphan: number;
  latestAt: string | null;
}

export const TRIAGE_COUNTER_GLYPH = '⚠';
export const TRIAGE_ORPHAN_GLYPH = '✋';

/** Amber is the resting colour; red is reserved for a row unhedged RIGHT NOW. */
export const TRIAGE_AMBER_HEX = VENUE_WARN_COLOR_HEX;
export const TRIAGE_RED_HEX = UNHEDGED_COLOR_HEX;

/**
 * Spec §6.3, verbatim. Every orphan mark carries it: an orphaned fill is
 * attributed to a PAIR and never to a rule, so every rule on that pair shows
 * the same mark and the operator must not read N marks as N incidents.
 */
export const UNATTRIBUTED_ORPHAN_NOTE =
  "This fill could not be attributed to a rule — it is on this pair's market. Another rule on the same pair shows the same mark.";

export function hasTriageMarks(
  counts: TriageRowCounts | undefined
): counts is TriageRowCounts {
  return !!counts && (counts.counter > 0 || counts.orphan > 0);
}

/** Total unread cards behind a row's mark — the `triage` sort key's value. */
export function triageMarkTotal(counts: TriageRowCounts | undefined): number {
  return hasTriageMarks(counts) ? counts.counter + counts.orphan : 0;
}

/**
 * `⚠ 2` for counter-only, `✋ 1` for orphan-only, `⚠2 ✋1` when both — the
 * tighter form only when the chip has to carry two numbers.
 */
export function triageChipLabel(counts: TriageRowCounts): string {
  const both = counts.counter > 0 && counts.orphan > 0;
  if (both) {
    return `${TRIAGE_COUNTER_GLYPH}${counts.counter} ${TRIAGE_ORPHAN_GLYPH}${counts.orphan}`;
  }
  if (counts.counter > 0) return `${TRIAGE_COUNTER_GLYPH} ${counts.counter}`;
  return `${TRIAGE_ORPHAN_GLYPH} ${counts.orphan}`;
}

/** Relative "5m ago" — no date lib in the repo, so inline (TriageTab precedent). */
export function triageTimeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const plural = (n: number): string => (n === 1 ? '' : 's');

/**
 * Condensed tray-row content. Exported so the opener table — which owns its
 * own `<Tooltip>` because of the hover ban — renders the same words.
 */
export function triageTooltipText(
  counts: TriageRowCounts,
  opts?: {
    unattributedNote?: boolean;
    /** A group badge aggregates many rules and pairs, so it says neither. */
    subject?: 'row' | 'group';
  }
): string {
  const inGroup = opts?.subject === 'group';
  const parts: string[] = [];
  if (counts.counter > 0) {
    parts.push(
      `${counts.counter} unread stuck-counter card${plural(counts.counter)} ${
        inGroup ? 'in this group' : 'on this rule'
      } — a filled maker leg whose counter never completed.`
    );
  }
  if (counts.orphan > 0) {
    parts.push(
      `${counts.orphan} unread orphaned automation fill${plural(
        counts.orphan
      )} ${inGroup ? 'in this group' : 'on this pair'}.`
    );
    if (opts?.unattributedNote) parts.push(UNATTRIBUTED_ORPHAN_NOTE);
  }
  const age = triageTimeAgo(counts.latestAt);
  if (age) parts.push(`Latest ${age}.`);
  parts.push('Click to open the triage tray.');
  return parts.join(' ');
}
